import { lazy, Suspense } from 'react'
import { Navigate } from 'react-router-dom'
import { PartialRouteObject, useRoutes } from 'react-router'
import SidebarLayout from 'src/layouts/SidebarLayout'
import SuspenseLoader from 'src/components/SuspenseLoader'
import { ErrorCard } from './components/Error/ErrorCard'
import {
  CONNECTED_TO_UNSUPPORTED_NETWORK_MESSAGE,
  CONNECTED_TO_UNSUPPORTED_NETWORK_TITTLE,
  CONNECT_YOUR_WALLET_MESSAGE,
  CONNECT_YOUR_WALLET_TITTLE
} from './utils/commons'
import { useWeb3Context } from './contexts/web3Context'

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  )

// Dashboards

const Launchpad = Loader(lazy(() => import('src/content/dashboards/Launchpad')))
const PresaleDetails = Loader(lazy(() => import('src/content/dashboards/PresaleDetails')))
const PresaleLockerDetails = Loader(
  lazy(() => import('src/content/dashboards/PresaleDetails/LockerDetails'))
)
const LaunchpadManager = Loader(lazy(() => import('src/content/dashboards/ManageLaunchpad')))
const Admin = Loader(lazy(() => import('src/content/dashboards/AdminPresale')))
const Locker = Loader(lazy(() => import('src/content/dashboards/Locker')))
const LockerDetails = Loader(lazy(() => import('src/content/dashboards/Locker/Details')))
const LockerLpTokens = Loader(lazy(() => import('src/content/dashboards/LockerLpTokens')))
const LockTokens = Loader(lazy(() => import('src/content/dashboards/LockTokens')))
const TermsAndConditions = Loader(lazy(() => import('src/content/dashboards/TermsAndConditions')))
const PrivacyPolicy = Loader(lazy(() => import('src/content/dashboards/PrivacyPolicy')))

export const enum PATHS {
  launchpad = 'launchpad',
  presale = 'presale',
  presaleLockerDetails = '/presale/lockerdetails',
  startLaunchpad = 'start-launchpad',
  adminLaunchpad = 'admin-launchpad',
  locker = 'locker',
  LockerDetails = '/locker/details',
  lockerLpTokens = 'lp-tokens',
  lockTokens = 'lock-tokens',
  termsAndConditions = 'termsandconditions',
  privacyPolicy = 'privacypolicy'
}

const Web3NotConnected = () => {
  const { isWrongNetwork } = useWeb3Context()

  return (
    <ErrorCard
      title={
        isWrongNetwork
          ? CONNECTED_TO_UNSUPPORTED_NETWORK_TITTLE
          : CONNECT_YOUR_WALLET_TITTLE
      }
      message={
        isWrongNetwork
          ? CONNECTED_TO_UNSUPPORTED_NETWORK_MESSAGE
          : CONNECT_YOUR_WALLET_MESSAGE
      }
    />
  )
}

const routes = (isConnected: boolean): PartialRouteObject[] => [
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/launchpad" replace />
      },
      {
        path: PATHS.launchpad,
        element: <Launchpad />
      },
      {
        path: `${PATHS.presale}/:pId`,
        element: <PresaleDetails />
      },
      {
        path: `${PATHS.presaleLockerDetails}/:pId`,
        element: <PresaleLockerDetails />
      },
      {
        path: PATHS.startLaunchpad,
        element: <LaunchpadManager />
      },
      {
        path: `${PATHS.adminLaunchpad}/:pId`,
        element: <Admin />
      },
      {
        path: `${PATHS.locker}`,
        element: isConnected ? <Locker /> : <Web3NotConnected />
      },
      {
        path: `${PATHS.LockerDetails}/:pId`,
        element: isConnected ? <LockerDetails /> : <Web3NotConnected />
      },
      {
        path: `${PATHS.lockerLpTokens}`,
        element: isConnected ? <LockerLpTokens /> : <Web3NotConnected />
      },
      {
        path: `${PATHS.lockTokens}`,
        element: isConnected ? <LockTokens /> : <Web3NotConnected />
      },
      {
        path: PATHS.termsAndConditions,
        element: <TermsAndConditions />
      },
      {
        path: PATHS.privacyPolicy,
        element: <PrivacyPolicy />
      }
    ]
  }
]

export const RoutesElement = () => {
  const { isConnected } = useWeb3Context()

  return useRoutes(routes(isConnected))
}

export default RoutesElement;
